import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useEffect, useMemo } from "react";

import { AddonStickyActions } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/AddonStickyActions";
import { OfferActionsLayout } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/OfferActionsLayout";
import { ProcessingPaymentLoader } from "@/react/components/shared/Paywalls/ProcessingPaymentLoader";

import { CheckoutDescription } from "../../../Checkout/CheckoutPage/CheckoutDescription";
import { PaywallCustomization } from "../../../Checkout/CheckoutPage/CheckoutDescription/PaywallCustomization";
import { usePaywallCustomizationContext } from "../../../Checkout/CheckoutPage/CheckoutDescription/PaywallCustomization/PaywallCustomizationProvider";
import {
  CustomizationLine,
  isBenefitsEnabled,
  isTestimonialsEnabled,
} from "./components/CustomizationLine";
import { FadeEffect } from "./components/FadeEffect";
import { ConfirmAndDeclineButton } from "./ConfirmAndDeclineButton";
import { useConfirmAndDeclineButton } from "./hooks/useConfirmAndDeclineButton";
import { useOfferPageContext } from "./OfferPageProvider";

interface OfferPageProps {
  // We need to add this artificial spacing because the upsell paywall form
  // doesn't behave like a desktop, but we should render it as if it were.
  artificialPadding?: "px-12";
  className?: string;
  isFullscreenPreview?: boolean;
  isPreviewing?: boolean;
}

export const OfferPage = Object.assign(
  ({
    artificialPadding,
    isPreviewing,
    isFullscreenPreview,
    className,
  }: OfferPageProps) => {
    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

    const { isProcessingAttempt } = useConfirmAndDeclineButton();
    const { paywallCustomization } = usePaywallCustomizationContext();

    const hasCustomizationsOutsideBlock = useMemo(
      () =>
        isBenefitsEnabled(paywallCustomization) ||
        isTestimonialsEnabled(paywallCustomization),
      [paywallCustomization],
    );

    return (
      <OfferActionsLayout
        actions={<ConfirmAndDeclineButton className="mx-auto max-w-[540px]" />}
        stickyActions={<AddonStickyActions />}
      >
        <div
          className={classNames(
            "flex flex-col items-center gap-y-16 pt-0",
            className,
            {
              "rounded-b-xl": isPreviewing && hasCustomizationsOutsideBlock,
              "bg-white [&>*:last-child]:pb-20 rounded-t-xl":
                hasCustomizationsOutsideBlock,
              "bg-transparent": !hasCustomizationsOutsideBlock,
            },
          )}
          data-testid="upsell-offer-page"
        >
          <ProcessingPaymentLoader shouldShow={isProcessingAttempt} />
          <CheckoutDescription.Offer
            useHooks={useOfferPageContext}
            artificialPadding={artificialPadding}
            className={classNames({
              "[&>*:first-child]:rounded-t-xl":
                isPreviewing &&
                !isFullscreenPreview &&
                hasCustomizationsOutsideBlock,
              "[&>*:first-child]:rounded-t-xl [&>*:last-child]:rounded-b-xl":
                isPreviewing &&
                !isFullscreenPreview &&
                !hasCustomizationsOutsideBlock,
            })}
          />
          <CustomizationLine
            feature="benefits"
            className={classNames("px-5 md:px-0", artificialPadding)}
          >
            <PaywallCustomization.Benefits />
          </CustomizationLine>
          <CustomizationLine
            feature="testimonials"
            className={classNames("px-5 md:px-0", artificialPadding)}
          >
            <PaywallCustomization.Testimonials />
          </CustomizationLine>
        </div>
      </OfferActionsLayout>
    );
  },
  {
    Skeleton: () => (
      <FadeEffect>
        <div className="flex flex-col pt-12 pb-20 items-center px-5 gap-y-16">
          <CheckoutDescription.Offer.Skeleton />
        </div>
      </FadeEffect>
    ),
  },
);
